import FailedToFetchDataEmptyState from '@app/src/components/EmptyState/FailedToFetchDataEmptyState'
import Markdown from '@app/src/components/Markdown'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import type { SxProps } from '@mui/material'
import { Box, Stack, Tooltip, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import React from 'react'

interface StatisticsCardProps {
  title: string
  children: React.ReactNode
  isEmptyState?: boolean
  withBorder?: boolean
  justifyContent?: 'center' | 'left' | 'right'
  action?: React.ReactNode
  helperText?: React.ReactNode
  loading?: {
    isLoading: boolean
    skeleton: React.ReactNode
  }
  isError?: boolean
  footer?: React.ReactNode
  titleAdornment?: React.ReactNode
  sx?: SxProps
  onClick?: () => void
}

const StatisticsCard: React.FC<StatisticsCardProps> = ({
  title,
  action,
  children,
  isEmptyState,
  withBorder,
  justifyContent = 'center',
  helperText,
  loading,
  isError,
  footer,
  titleAdornment,
  sx,
  onClick,
}) => {
  const { palette } = useTheme()
  const clickableProps = onClick && {
    onClick,
    role: 'button',
    tabIndex: 0,
    onKeyDown: (event: React.KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') return
      event.preventDefault()
      onClick()
    },
  }

  return (
    <Box
      py={2}
      px={3}
      bgcolor={isEmptyState ? 'secondary.main' : 'background.default'}
      borderRadius="8px"
      height="100%"
      width="100%"
      border={withBorder ? `1px ${palette.grey[300]} solid` : 'none'}
      display="flex"
      flexDirection="column"
      overflow="hidden"
      sx={sx}
      {...clickableProps}
    >
      <Box display="flex" justifyContent="space-between" color="common.white" sx={{ minHeight: '28px' }}>
        <Stack direction="row" spacing={1} py={0.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          {titleAdornment}
          <Typography variant="overline" color={isEmptyState ? 'inherit' : 'textSecondary'} noWrap>
            <Markdown>{title}</Markdown>
          </Typography>
          {helperText && (
            <Tooltip title={helperText} placement="right" arrow>
              <InfoOutlinedIcon
                sx={({ palette }) => ({
                  color: palette.text.secondary,
                  '&:hover': { backgroundColor: 'grey.200', borderRadius: 4, cursor: 'pointer' },
                })}
                fontSize="small"
              />
            </Tooltip>
          )}
        </Stack>

        {action}
      </Box>
      <Box display="flex" justifyContent={justifyContent} flexGrow={1}>
        {isError ? <FailedToFetchDataEmptyState /> : loading?.isLoading ? loading.skeleton : children}
      </Box>
      {footer && <Box sx={{ mx: -3, mb: -2 }}>{footer}</Box>}
    </Box>
  )
}

export default StatisticsCard
